import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useAuthStore } from 'features/auth/stores/authStore';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { apiClient } from 'api';

/**
 * OAuth Callback Handler
 * Processes the JWT token from the Google OAuth callback and stores it
 */
const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();

  useEffect(() => {
    const processCallback = async () => {
      const token = searchParams.get('token');
      const error = searchParams.get('error');
      const returnUrl = searchParams.get('returnUrl') || ROUTE_PATHS.TRACKER;

      // Handle OAuth errors
      if (error) {
        console.error('OAuth error:', error);
        navigate(`/login?error=${encodeURIComponent(error)}`);
        return;
      }

      // Handle missing token
      if (!token) {
        console.error('No token received from OAuth callback');
        navigate('/login?error=no_token');
        return;
      }

      try {
        // Store the JWT token (refresh token is the same for now)
        // This allows the axios interceptor to use it for requests
        setAuth(token, token, null);

        // Fetch user profile using the new JWT
        const response = await apiClient.get('/auth/me');
        const userData = response.data.data;

        // Transform the backend user response to match the frontend User interface
        const user = {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          avatarUrl: userData.avatarUrl,
          role: (userData.role || 'TeamMember') as
            | 'Owner'
            | 'Admin'
            | 'ProjectManager'
            | 'TeamMember',
          workspaceId: userData.workspaceId || '',
        };

        // Update auth store with user profile
        setAuth(token, token, user);

        // Redirect to original destination
        navigate(returnUrl, { replace: true });
      } catch (err) {
        console.error('Failed to process OAuth callback:', err);
        navigate(`/login?error=processing_failed`);
      }
    };

    processCallback();
  }, [searchParams, navigate, setAuth]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
        Completing sign in...
      </Typography>
    </Box>
  );
};

export default OAuthCallbackPage;
